import { useLocation, useNavigate } from "react-router-dom";

import { InvoiceAddEditButtons } from "./InvoiceAddEditButtons";
import { InvoiceDownloadButton } from "./InvoiceDownloadButton";
import { navigateToInvoicePage } from "./navigateToInvoicePage";

import { MonthYearSelector } from "@/components/ui/v2/month-year-selector";
import { TableToolbar } from "@/components/ui/v2/table/table-toolbar";
import { ROUTES } from "@/constants/routes";

type InvoiceEntityTableToolbarProps = {
  onSearchChange: (value: string) => void;
  onDownload: () => void;
  searchValue?: string;
  year: string;
  month: string;
  entityId: string;
  routeSegment: string;
  locationStateKey: string;
  maxDate?: [string, string];
  currentPageLabel?: string;
  entityName?: string;
  isEditMode: boolean;
  onEditModeChange: (isEditMode: boolean) => void;
  onSave: () => void;
  onCancel: () => void;
  isEditDisabled?: boolean;
  isSaving?: boolean;
  hasData?: boolean;
  isDownloading?: boolean;
};

export function InvoiceEntityTableToolbar({
  onSearchChange,
  onDownload,
  searchValue,
  year,
  month,
  entityId,
  routeSegment,
  locationStateKey,
  maxDate,
  currentPageLabel,
  entityName,
  isEditMode,
  onEditModeChange,
  onSave,
  onCancel,
  isEditDisabled = false,
  isSaving = false,
  hasData = false,
  isDownloading = false,
}: InvoiceEntityTableToolbarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleMonthYearChange = (yearMonth: [string, string]) => {
    const [newYear, newMonth] = yearMonth;
    navigateToInvoicePage({
      navigate,
      location,
      routeSegment,
      entityId,
      year: newYear,
      month: newMonth,
      locationStateKey,
      currentPageLabel,
      entityName,
    });
  };

  // Determine if we're on the invoiced tab
  const isInvoicedTab = location.pathname.includes(`/${ROUTES.invoiced}`);
  const currentTab = isInvoicedTab ? "invoiced" : "toDo";
  
  // Only show download button for job boards, not channels
  const showDownloadButton = routeSegment !== ROUTES.channels;

  return (
    <div className="flex flex-col w-full gap-4">
      <div className="flex justify-end">
        <div className="flex items-center gap-2">
          {showDownloadButton && (
            <InvoiceDownloadButton
              onDownload={onDownload}
              isLoading={isDownloading}
              hasData={hasData}
            />
          )}
          <InvoiceAddEditButtons
            isEditMode={isEditMode}
            onEditModeChange={onEditModeChange}
            onSave={onSave}
            onCancel={onCancel}
            isEditDisabled={isEditDisabled}
            isSaving={isSaving}
            hasData={hasData}
            tabType={currentTab}
          />
        </div>
      </div>
      <div className="flex items-center gap-2 justify-between">
        <TableToolbar
          onSearchChange={onSearchChange}
          searchValue={searchValue}
        />
        <MonthYearSelector
          value={[year, month]}
          onChange={handleMonthYearChange}
          maxDate={maxDate}
        />
      </div>
    </div>
  );
}
